import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Popconfirm, Select, Table, Tag } from "antd";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag } from "@/shared/components/StatusTag";
import { TableToolbar } from "@/shared/components/TableToolbar";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import { TASK_CATEGORY_COLORS, TASK_CATEGORY_LABELS, TASK_CATEGORY_OPTIONS } from "../constants";
import { useDeleteTaskType, useTaskTypes } from "../hooks/useTaskTypes";
import { useTaskTypesStore } from "../store/task-types.store";
import type { TaskCategory, TaskType } from "../types";

export function TaskTypeTable() {
	const { onOpen } = useTaskTypesStore();
	const { page: currentPage, limit: pageSize, setPage, getFilter, setFilters } = useUrlTableState();
	const category = getFilter("category") as TaskCategory | undefined;
	const { data, isLoading } = useTaskTypes({ page: currentPage, limit: pageSize, category });
	const { mutate: remove } = useDeleteTaskType();
	const items = data?.data?.items ?? [];
	const total = data?.data?.meta?.total ?? 0;

	return (
		<div>
			<TableToolbar
				filters={
					<Select
						placeholder="Toifa bo'yicha filtr"
						allowClear
						value={category}
						onChange={(value) => setFilters({ category: value ?? null })}
						options={TASK_CATEGORY_OPTIONS}
						style={{ width: 220 }}
						id="task-types-category-filter"
					/>
				}
			/>
			<Table
				loading={isLoading}
				locale={{
					emptyText: <EmptyState description="Topshiriq turlari topilmadi" />,
				}}
				pagination={tablePagination({
					page: currentPage,
					limit: pageSize,
					total,
					onChange: (page, size) => setPage(page, size ?? pageSize),
				})}
				size="small"
				scroll={{ x: "max-content" }}
				dataSource={items}
				rowKey="id"
				columns={[
					{
						key: "#",
						title: "#",
						width: 60,
						align: "center",
						render: (_, __, index) => formatTableIndex(currentPage, pageSize, index),
					},
					{
						key: "name",
						title: "Nomi",
						dataIndex: "name",
					},
					{
						key: "category",
						title: "Toifa",
						dataIndex: "category",
						width: 200,
						render: (value: TaskCategory) => (
							<Tag color={TASK_CATEGORY_COLORS[value] ?? "default"}>
								{TASK_CATEGORY_LABELS[value] ?? value}
							</Tag>
						),
					},
					{
						key: "defaultPriority",
						title: "Ustuvorlik",
						dataIndex: "defaultPriority",
						width: 110,
					},
					{
						key: "isActive",
						title: "Holat",
						dataIndex: "isActive",
						width: 80,
						render: (isActive: boolean) => (
							<StatusTag tone={isActive ? "success" : "default"}>
								{isActive ? "Faol" : "Nofaol"}
							</StatusTag>
						),
					},
					{
						key: "actions",
						title: "Amallar",
						width: 80,
						align: "center",
						render: (_: unknown, record: TaskType) => (
							<Dropdown
								menu={{
									items: [
										{
											key: "edit",
											icon: <EditOutlined />,
											label: "Tahrirlash",
											onClick: () => onOpen(record),
										},
										{
											key: "delete",
											danger: true,
											label: (
												// biome-ignore lint/a11y/useKeyWithClickEvents: dropdown menu item
												// biome-ignore lint/a11y/noStaticElementInteractions: dropdown menu item
												<div onClick={(e) => e.stopPropagation()}>
													<Popconfirm
														title="O'chirish"
														description="Rostdan ham o'chirmoqchimisiz?"
														onConfirm={() => remove(record.id)}
														okText="Ha"
														cancelText="Yo'q"
														okButtonProps={{ danger: true }}
													>
														<div
															style={{
																width: "100%",
																display: "flex",
																alignItems: "center",
																gap: "8px",
															}}
														>
															<DeleteOutlined />
															<span>O&apos;chirish</span>
														</div>
													</Popconfirm>
												</div>
											),
										},
									],
								}}
								trigger={["click"]}
								placement="bottomRight"
							>
								<Button type="text" icon={<MoreOutlined />} size="small" />
							</Dropdown>
						),
					},
				]}
			/>
		</div>
	);
}
